import React, { useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Box,
  useMediaQuery,
} from "@mui/material";
import { BsCash, BsBarChart } from 'react-icons/bs';
import { FiHome } from "react-icons/fi";
import { IoGridOutline } from 'react-icons/io5';
import { MdOutlinePreview } from 'react-icons/md';
import theme from '../../theme';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TbCash, TbChartBar, TbEye, TbHome, TbLayoutGridAdd, TbTableHeart, TbRefresh, TbMessages, TbPackage, TbSchool, TbMessage } from 'react-icons/tb';

const iconStyle = {
  color: "#222222",
  fontSize: '16px',
  margin: '0px',
  "&:hover": {
    color: "var(--brand-color)",
  },
};

const BottomTab = ({
  handleProfileTabChange,
  updateViewType,
  setState,
  activeProductData,
  ...props
}) => {
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const windowWidth = window.innerWidth;
  const [searchParams] = useSearchParams();
  const current_page_view = searchParams.get("page");
  const creator_preview = Boolean(searchParams.get('creator_preview'));
  const isSubscribed = Boolean(searchParams.get('isSubscribed'));
  
  const handleTabChange = (tab) => {
    navigate(`?page=${tab}`);
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  const mainTabs = useMemo(() => {
    // console.log("activeProductData:", activeProductData); // Debug log
    
    // Define all possible product tabs
    const productTabs = {
      subscriptions: {
        id: 'subscriptions',
        title: 'Subscriptions',
        icon: TbRefresh,
        action: () => handleTabChange('subscriptions')
      },
      oneOnOneCoaching: {
        id: '1v1coaching',
        title: 'Coaching',
        icon: TbMessages,
        action: () => handleTabChange('1v1coaching')
      },
      community: {
        id: 'community',
        title: 'Community',
        icon: TbTableHeart,
        action: () => handleTabChange('community')
      },
      digitalproducts: {
        id: 'digital_products',
        title: 'Products',
        icon: TbPackage,
        action: () => handleTabChange('digital_products')
      },
      courses: {
        id: 'courses',
        title: 'Courses',
        icon: TbSchool,
        action: () => handleTabChange('courses')
      },
      messages: {
        id: 'messages',
        title: 'Messages',
        icon: TbMessage,
        action: () => handleTabChange('messages')
      }
    };
    
    // Initialize tabs with default tabs
    const defaultTabs = [
      {
        id: 'home',
        title: 'Home',
        icon: TbHome,
        action: () => {
          handleTabChange('home');
        }
      },
      {
        id: 'preview',
        title: 'Preview',
        icon: TbEye,
        action: () => {
          handleTabChange('preview');
        }
      },
      {
        id: 'platform',
        title: 'Platform',
        icon: TbTableHeart,
        action: () => {
          handleTabChange('platform')
        }
      },
      {
        id: 'payouts',
        title: 'Payouts',
        icon: TbCash,
        action: () => handleTabChange('payouts')
      },     
      {
        id: 'insights',
        title: 'Insights',
        icon: TbChartBar,
        action: () => handleTabChange('insights')
      }, 
      {
        id: 'more',
        title: 'More',
        icon: TbLayoutGridAdd,
        action: () => handleTabChange('more')
      }
    ];
    
    // Define priority order
    const priorityOrder = [
      'subscriptions',
      'oneOnOneCoaching', 
      'community',
      'digitalproducts',
      'courses',
      'messages'
    ];
    
    // Check if any product is active
    let hasActiveProduct = false;
    let priorityTab = null;
    
    // Get first active product from activeProductData
    if (activeProductData && activeProductData.active_products && activeProductData.active_products.length > 0) {
      const products = activeProductData.active_products[0];
      
      // Check each product type in priority order
      for (const productType of priorityOrder) {
        
        if (productType === 'subscriptions' && products.subscriptions?.active) {
          priorityTab = productTabs.subscriptions;
          hasActiveProduct = true;
          break;
        }
        if (productType === 'oneOnOneCoaching' && products.oneOnOneCoaching?.active) {
          priorityTab = productTabs.oneOnOneCoaching;
          hasActiveProduct = true;
          break;
        }
        if (productType === 'community' && products.community?.active) {
          priorityTab = productTabs.community;
          hasActiveProduct = true;
          break;
        }
        if (productType === 'digitalproducts' && products.digitalproducts?.active) {
          priorityTab = productTabs.digitalproducts;
          hasActiveProduct = true;
          break;
        }
        if (productType === 'courses' && products.courses?.active) {
          priorityTab = productTabs.courses;
          hasActiveProduct = true;
          break;
        }
        if (productType === 'messages' && products.messages?.active) {
          priorityTab = productTabs.messages;
          hasActiveProduct = true;
          break;
        }
      }
    }
  
    
    // Insert priority tab after platform tab if any product is active
    if (hasActiveProduct && priorityTab) {
      // Create a new array with the priority tab inserted at position 3
      const result = [
        defaultTabs[0],
        defaultTabs[1],
        defaultTabs[2],
        props.launched_influencer ? defaultTabs[4] : defaultTabs[3],
        priorityTab,
        defaultTabs[5]
      ];
      return result;
    }
    return defaultTabs;
  }, [activeProductData, handleTabChange]);

  const handleTabClick = useCallback((tab) => {
    tab.action();
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // set Initial tab to Platform
  useEffect(() => {
    if (windowWidth < 1280 && (!creator_preview || !isSubscribed) && !current_page_view) {
      const initialTab = current_page_view
        ? mainTabs.find(tab => tab.id === current_page_view)
        : mainTabs[2];
      if (initialTab) handleTabClick(initialTab);
    }
  }, [windowWidth, creator_preview, isSubscribed, current_page_view, mainTabs, handleTabClick]);

  // Hide on desktop
  if (windowWidth >= 1280) return null;

  return (
    <Box sx={{ position: 'fixed', bottom: 0, left: 0, width: '100%', zIndex: 1299 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          height: '60px',
          borderTop: '1px solid #E4EBF6',
          bgcolor: '#ffffff',
          boxShadow: '0px -6px 10px rgba(0, 0, 0, 0.08)',
        }}
      >
        {mainTabs.map((tab) => (
          <Box
            key={tab.id}
            sx={{
              flex: 1,
              height: '100%',
              bgcolor: current_page_view === tab.id ? '#f6f7ff' : 'transparent',
              transition: 'background-color 0.3s ease',
            }}
          >
            <Button
              aria-label={tab.title}
              sx={{
                color: '#000000',
                minWidth: '0px',
                flexDirection: 'column',
                border: 'none',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '8px 6px 5px 6px',
                fontSize: windowWidth < 340 ? '7px' : windowWidth < 360 ? '8px' : windowWidth < 380 ? '9px' : windowWidth < 400 ? '10px' : windowWidth < 420 ? '11px' : '12px',
                fontWeight: '500',
                fontFamily: 'Roboto, sans-serif',
                width: '100%',
                height: '100%',
                transition: 'all 0.15s ease-in-out',
                '&:hover': { bgcolor: 'transparent' },
              }}
              onClick={() => handleTabClick(tab)}
            >
              <tab.icon style={{ ...iconStyle, color: current_page_view === tab.id ? 'var(--brand-color)' : '#000000' }} />
              <span style={{ textTransform: 'none', marginTop: '2px' }}>{tab.title}</span>
            </Button>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default BottomTab;